import { FC, useState, useEffect, useRef } from 'react';
import { Box, Typography, useTheme, Button } from '@mui/material';
import useClipboard from '@/modules/_core/hooks/useClipboard';
import { Colors } from '@sweatpals/react-ui-kit';

export interface QrCodeProps {
	link: string;
	size?: number;
	showQrSquare?: boolean;
	backgroundColor?: string;
}

const QrCode: FC<QrCodeProps> = ({ link, size = 300, showQrSquare = true }) => {
	const theme = useTheme();

	const qrRef = useRef(null);

	const [qrCode, setQrCode] = useState(null);

	useEffect(() => {
		(async () => {
			if (!showQrSquare) {
				return;
			}

			const { default: QRCodeStyling } = await import('qr-code-styling');

			const qrCode = new QRCodeStyling({
				width: size,
				height: size,
				dotsOptions: {
					color: Colors['393739'],
					// type: "dots",
					type: 'extra-rounded',
				},
				cornersSquareOptions: {
					color: Colors['110A20'],
					type: 'extra-rounded',
				},
				cornersDotOptions: {
					// type: 'dot',
				},
				qrOptions: {
					errorCorrectionLevel: 'H',
				},
			});

			setQrCode(qrCode);

			if (qrRef.current) {
				qrCode.append(qrRef.current);
			}
		})();
	}, [link, size, showQrSquare]);

	useEffect(() => {
		qrCode?.update({
			data: link,
		});
	}, [qrCode, link]);

	const [wasCopied, setWasCopied] = useState(false);
	const { copyTextToClipboard } = useClipboard();

	const addLinkToClipboard = async () => {
		await copyTextToClipboard(link);
		setWasCopied(true);
	};

	const goToLink = () => {
		window.open(link, '_blank').focus();
	};

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: '25px',
				justifyContent: 'center',
				alignItems: 'center',
			}}
		>
			{showQrSquare && (
				<Box sx={{ p: '2px', backgroundColor: '#000', borderRadius: '20px', boxShadow: `6px 6px #000` }}>
					<Box
						sx={{
							backgroundColor: theme.palette.primary.light,
							padding: '5px',
							borderRadius: '20px',
							overflow: 'hidden',
						}}
					>
						<div ref={qrRef} style={{ height: size, width: size }} />
					</Box>
				</Box>
			)}

			<Button
				onClick={showQrSquare ? addLinkToClipboard : goToLink}
				sx={{
					backgroundColor: 'white',
					py: '14px',
					cursor: 'pointer',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					gap: '4px',
					borderRadius: '30px',
					border: '1px solid #000',
					width: '100%',
					userSelect: 'none',

					'&:hover': {
						backgroundColor: 'white',
					},

					'&:active': {
						backgroundColor: 'white',
					},
				}}
			>
				<Typography
					sx={{
						fontFamily: 'Integral CF',
						fontWeight: 500,
						color: theme.palette.primary.shadow,
						fontSize: { xs: '14px', sm: '16px' },
						lineHeight: '1.2em',
						textAlign: 'center',
					}}
				>
					{showQrSquare ? (wasCopied ? 'Link Copied' : 'Copy Link') : 'Open in App'}
				</Typography>
			</Button>
		</Box>
	);
};

export default QrCode;
